import { useState, MouseEvent } from 'react'

import { Product, ProductMedia } from '../../utils/types/Product'
import useIsOnMobile from '../../services/useIsOnMobile'
import { ScreenSize } from '../../utils/constants'

interface UseImageNavigationProps {
  product: Product
}

interface UseImageNavigationReturn {
  // State
  currentImageIndex: number
  isImageAnimating: boolean
  isHovered: boolean
  setIsHovered: (hovered: boolean) => void

  // Computed values
  media: ProductMedia[]
  firstImage?: ProductMedia
  secondImage?: ProductMedia
  hasSecondImage: boolean
  currentImage?: ProductMedia
  isOnMobile: boolean

  // Event handlers
  handleImageNavigation: (event: MouseEvent<HTMLButtonElement>) => void
  getMouseEnterHandler: () => (() => void) | undefined
  getMouseLeaveHandler: () => (() => void) | undefined

  // Animation class name
  getImageClassName: () => string
}

const useImageNavigation = ({
  product,
}: UseImageNavigationProps): UseImageNavigationReturn => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isImageAnimating, setIsImageAnimating] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const isOnMobile = useIsOnMobile(ScreenSize.md)

  const media = product?.media.filter((image) => image['media.type'] === 'default') || []

  const firstImage = media[0]
  const secondImage = media[1]
  const hasSecondImage = media.length > 1 && firstImage?.['media.url'] !== secondImage?.['media.url']

  // For desktop: use hover logic, for mobile: use arrow navigation
  let currentImage
  if (isOnMobile) {
    currentImage = media[currentImageIndex] || media[0]
  } else {
    currentImage = isHovered && hasSecondImage ? secondImage : firstImage
  }

  const handleImageNavigation = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isImageAnimating) return

    setIsImageAnimating(true)
    const nextIndex = (currentImageIndex + 1) % media.length
    setCurrentImageIndex(nextIndex)

    setTimeout(() => {
      setIsImageAnimating(false)
    }, 500) // Match the animation duration
  }

  const getMouseEnterHandler = () => (
    !isOnMobile && hasSecondImage ? () => setIsHovered(true) : undefined)

  const getMouseLeaveHandler = () => (
    !isOnMobile && hasSecondImage ? () => setIsHovered(false) : undefined)

  const getImageClassName = () => ((isOnMobile && isImageAnimating)
        || (!isOnMobile && isHovered && hasSecondImage)
    ? 'image-fade-in-out'
    : '')

  return {
    currentImageIndex,
    isImageAnimating,
    isHovered,
    setIsHovered,
    media,
    firstImage,
    secondImage,
    hasSecondImage,
    currentImage,
    isOnMobile,
    handleImageNavigation,
    getMouseEnterHandler,
    getMouseLeaveHandler,
    getImageClassName,
  }
}

export default useImageNavigation
